import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Select, { type StylesConfig } from 'react-select';
import { buildReportSelectStyles } from '@/components/Reports/reportSelectStyles';

import moment from 'moment';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);
import ReportService from '@services/ReportService';
import { ClearIndicatorComponent as ClearIndicator } from '@/components/Reports/ClearIndicatorComponent';
import './MonthlyPerformanceReport.css';

// ─── Types ────────────────────────────────────────────────────────────────────

interface SelectOption {
  value: number | string;
  label: string;
}

type PerformanceType = 'Daily' | 'Weekly' | 'Monthly' | 'Quarterly' | 'Semester' | 'Annual';

interface PerformanceRow {
  id: number;
  month: string;
  workingCapacity: number;
  loggedHours: string;
  ptoDays: number;
  productivity: string;
  efficiency: string;
  hasLogs: boolean;
}

type SortCol =
  | 'month'
  | 'workingCapacity'
  | 'loggedHours'
  | 'ptoDays'
  | 'productivity'
  | 'efficiency';
type SortDir = 'asc' | 'desc';

interface ChartData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor: string;
    fill: boolean;
    hidden: boolean;
    pointRadius: number;
    borderWidth: number;
  }[];
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PERFORMANCE_TYPES: SelectOption[] = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Monthly', label: 'Monthly' },
  { value: 'Quarterly', label: 'Quarterly' },
  { value: 'Semester', label: 'Semester' },
  { value: 'Annual', label: 'Annual' },
];

const COLUMN_LABELS: Record<PerformanceType, string> = {
  Daily: 'Day',
  Weekly: 'Week',
  Monthly: 'Month',
  Quarterly: 'Quarter',
  Semester: 'Half Year',
  Annual: 'Year',
};

// ─── Date input ───────────────────────────────────────────────────────────────

interface MprDateInputProps {
  value?: string;
  onClick?: () => void;
  onClear: () => void;
}

const MprDateInput = React.forwardRef<HTMLInputElement, MprDateInputProps>(
  ({ value, onClick, onClear }, ref) => (
    <div className="mpr-date-input-wrapper">
      <input
        ref={ref}
        type="text"
        value={value || ''}
        onClick={onClick}
        readOnly
        className="mpr-date-input"
        placeholder="MM/DD/YYYY"
        onChange={() => {}}
      />
      <button
        type="button"
        className="mpr-date-icon-btn"
        onClick={value ? onClear : onClick}
        tabIndex={-1}
      >
        <i className="fa fa-calendar" style={{ fontSize: 12 }} />
      </button>
    </div>
  ),
);
MprDateInput.displayName = 'MprDateInput';

// ─── Sort icon ────────────────────────────────────────────────────────────────

const SortIcon: React.FC<{ col: string; sorted: string; dir: SortDir }> = ({
  col,
  sorted,
  dir,
}) => (
  <i
    className={
      sorted === col ? (dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc') : 'fa fa-sort'
    }
    style={{ display: 'inline-block', width: 20 }}
  />
);

// ─── Component ────────────────────────────────────────────────────────────────

const MonthlyPerformanceReport: React.FC = () =>
{
  const isDark = document.documentElement.classList.contains('dark');
  const selectStyles = buildReportSelectStyles(isDark);
  const singleSelectStyles = selectStyles as StylesConfig<SelectOption, false>;
  const multiSelectStyles = selectStyles as StylesConfig<SelectOption, true>;

  // Filter options
  const [teamOptions, setTeamOptions] = useState<SelectOption[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<SelectOption[]>([]);
  const [cardTypeOptions, setCardTypeOptions] = useState<SelectOption[]>([]);
  const [projectOptions, setProjectOptions] = useState<SelectOption[]>([]);
  const [layerOptions, setLayerOptions] = useState<SelectOption[]>([]);

  // Selected filters
  const [selectedTeams, setSelectedTeams] = useState<SelectOption[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<SelectOption[]>([]);
  const [selectedCardTypes, setSelectedCardTypes] = useState<SelectOption[]>([]);
  const [selectedProjects, setSelectedProjects] = useState<SelectOption[]>([]);
  const [selectedLayers, setSelectedLayers] = useState<SelectOption[]>([]);
  const [selectedType, setSelectedType] = useState<SelectOption>(PERFORMANCE_TYPES[0]!);
  const [includeDisabledUsers, setIncludeDisabledUsers] = useState(false);

  // Dates — default: start of current week → today
  const [fromDate, setFromDate] = useState<Date | null>(() => moment().startOf('week').toDate());
  const [toDate, setToDate] = useState<Date | null>(() => new Date());
  const [fromDateEmpty, setFromDateEmpty] = useState(false);
  const [toDateEmpty, setToDateEmpty] = useState(false);
  const [fromDateLess, setFromDateLess] = useState(false);

  // Table
  const [tableData, setTableData] = useState<PerformanceRow[]>([]);
  const [sortCol, setSortCol] = useState<SortCol>('month');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [loading, setLoading] = useState(false);
  const [resultsFounds, setResultsFounds] = useState('Click apply filters to get results.');

  // Totals
  const [totalWorkingCapacity, setTotalWorkingCapacity] = useState(0);
  const [totalLoggedHours, setTotalLoggedHours] = useState(0);
  const [totalPtoDays, setTotalPtoDays] = useState(0);
  const [totalProductivity, setTotalProductivity] = useState(0);
  const [totalEfficiency, setTotalEfficiency] = useState(0);

  const [showDateRangeMsg, setShowDateRangeMsg] = useState(false);
  const dateRangeMsgTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Chart data
  const [chartData, setChartData] = useState<ChartData>({
    labels: [],
    datasets: [
      {
        label: 'Productivity',
        data: [],
        borderColor: 'rgba(154,82,227,1)',
        backgroundColor: 'rgba(154,82,227,0.2)',
        fill: false,
        hidden: false,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Efficiency',
        data: [],
        borderColor: 'rgba(192,148,214,1)',
        backgroundColor: 'rgba(192,148,214,0.2)',
        fill: false,
        hidden: false,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Working Capacity',
        data: [],
        borderColor: 'rgba(127,143,81,1)',
        backgroundColor: 'rgba(127,143,81,0.2)',
        fill: false,
        hidden: true,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'Logged Hours',
        data: [],
        borderColor: 'rgba(124,200,153,1)',
        backgroundColor: 'rgba(124,200,153,0.2)',
        fill: false,
        hidden: true,
        pointRadius: 0,
        borderWidth: 2,
      },
      {
        label: 'PTO Days',
        data: [],
        borderColor: 'rgba(124,197,220,1)',
        backgroundColor: 'rgba(124,197,220,0.2)',
        fill: false,
        hidden: true,
        pointRadius: 0,
        borderWidth: 2,
      },
    ],
  });

  // ── Initial data load ────────────────────────────────────────────────────

  useEffect(() =>
  {
    document.title = 'Corgee';
  }, []);

  useEffect(() =>
  {
    ReportService.GetAllTeams()
      .then((data: any[]) =>
        setTeamOptions((data || []).map(t => ({ value: t.BasicFieldValueId, label: t.Value }))),
      )
      .catch(() => {});

    ReportService.GetEfficiencyAsignedTo({
      teams: '',
      artistCategory: '',
      department: '',
      getAll: false,
    })
      .then((data: any[]) =>
        setEmployeeOptions((data || []).map((e: any) => ({ value: e.PersonID, label: e.Name }))),
      )
      .catch(() => {});

    ReportService.GetAllCard()
      .then((data: any[]) =>
        setCardTypeOptions((data || []).map(c => ({ value: c.SideMenuId, label: c.Text }))),
      )
      .catch(() => {});

    ReportService.GetAllProject({ GroupByDetailId: 0, GroupByLayerId: 0, GroupByValueField: '' })
      .then((data: any) =>
        setProjectOptions(
          (data?.Projects || []).map((p: any) => ({ value: p.ProjectID, label: p.Name })),
        ),
      )
      .catch(() => {});

    ReportService.GetLayers()
      .then((data: any[]) =>
        setLayerOptions((data || []).map(l => ({ value: l.LayerId, label: l.Name }))),
      )
      .catch(() => {});
  }, []);

  // ── Reload employees when teams change ───────────────────────────────────

  const loadEmployees = useCallback((teams: (number | string)[], inactive: boolean) =>
  {
    ReportService.GetEfficiencyAsignedTo({
      teams: teams.join(','),
      artistCategory: '',
      department: '',
      getAll: inactive,
    })
      .then((data: any[]) =>
        setEmployeeOptions((data || []).map((e: any) => ({ value: e.PersonID, label: e.Name }))),
      )
      .catch(() => {});
  }, []);

  // ── Validate ─────────────────────────────────────────────────────────────

  const validate = useCallback((): boolean =>
  {
    let valid = true;
    if (!fromDate)
    {
      setFromDateEmpty(true);
      valid = false;
    }
    else setFromDateEmpty(false);
    if (!toDate)
    {
      setToDateEmpty(true);
      valid = false;
    }
    else setToDateEmpty(false);
    if (fromDate && toDate && moment(toDate).isBefore(moment(fromDate)))
    {
      setFromDateLess(true);
      return false;
    }
    setFromDateLess(false);
    if (fromDate && moment(fromDate).isBefore(moment().subtract(2, 'years')))
    {
      if (dateRangeMsgTimerRef.current) clearTimeout(dateRangeMsgTimerRef.current);
      setShowDateRangeMsg(true);
      dateRangeMsgTimerRef.current = setTimeout(() => setShowDateRangeMsg(false), 3000);
      return false;
    }
    return valid;
  }, [fromDate, toDate]);

  // ── Fill table ───────────────────────────────────────────────────────────

  const fillTable = useCallback((response: any) =>
  {
    const general: any[] = response?.general;
    if (!general?.length)
    {
      setResultsFounds('No results found');
      setTableData([]);
      setChartData(prev => ({
        labels: [],
        datasets: prev.datasets.map(ds => ({ ...ds, data: [] })),
      }));
      setTotalWorkingCapacity(0);
      setTotalLoggedHours(0);
      setTotalPtoDays(0);
      setTotalProductivity(0);
      setTotalEfficiency(0);
      return;
    }

    setResultsFounds('');
    let tWC = 0,
      tLH = 0,
      tPTO = 0,
      tProd = 0,
      tEff = 0,
      countWithLogs = 0;
    const rows: PerformanceRow[] = [];
    const labels: string[] = [];
    const dProductivity: number[] = [];
    const dEfficiency: number[] = [];
    const dWorkingCapacity: number[] = [];
    const dLoggedHours: number[] = [];
    const dPtoDays: number[] = [];

    for (const el of general)
    {
      const lh = parseFloat(el.loggedHours);
      const hasLogs = lh > 0;

      tWC += el.workingCapacity;
      tLH += lh;
      tPTO += el.ptoDays;
      if (hasLogs)
      {
        tProd += el.productivity;
        tEff += el.efficiency;
        countWithLogs++;
      }

      const month = selectedType.value === 'Weekly'
        ? el.month
          .replace('1eek', 'Week 1')
          .replace('2eek', 'Week 2')
          .replace('3eek', 'Week 3')
          .replace('4eek', 'Week 4')
          .replace('5eek', 'Week 5')
        : el.month;

      rows.push({
        id: el.monthId,
        month,
        workingCapacity: el.workingCapacity,
        loggedHours: lh.toFixed(2),
        ptoDays: el.ptoDays,
        productivity: parseFloat(el.productivity).toFixed(2),
        efficiency: parseFloat(el.efficiency).toFixed(2),
        hasLogs,
      });

      labels.push(month);
      dProductivity.push(parseFloat(el.productivity));
      dEfficiency.push(parseFloat(el.efficiency));
      dWorkingCapacity.push(el.workingCapacity);
      dLoggedHours.push(lh);
      dPtoDays.push(el.ptoDays);
    }

    setTableData(rows);
    setChartData(prev => ({
      labels,
      datasets: prev.datasets.map(ds =>
      {
        if (ds.label === 'Productivity') return { ...ds, data: dProductivity, pointRadius: 3 };
        if (ds.label === 'Efficiency') return { ...ds, data: dEfficiency, pointRadius: 3 };
        if (ds.label === 'Working Capacity')
          return { ...ds, data: dWorkingCapacity, pointRadius: 3 };
        if (ds.label === 'Logged Hours') return { ...ds, data: dLoggedHours, pointRadius: 3 };
        if (ds.label === 'PTO Days') return { ...ds, data: dPtoDays, pointRadius: 3 };
        return ds;
      }),
    }));
    setTotalWorkingCapacity(tWC);
    setTotalLoggedHours(parseFloat(tLH.toFixed(2)));
    setTotalPtoDays(tPTO);
    setTotalProductivity(countWithLogs > 0 ? parseFloat((tProd / countWithLogs).toFixed(2)) : 0);
    setTotalEfficiency(countWithLogs > 0 ? parseFloat((tEff / countWithLogs).toFixed(2)) : 0);
  }, [selectedType]);

  // ── Apply filters ────────────────────────────────────────────────────────

  const applyFilters = useCallback(async () =>
  {
    if (!validate()) return;
    setLoading(true);
    setTableData([]);
    setChartData(prev => ({
      labels: [],
      datasets: prev.datasets.map(ds => ({ ...ds, data: [] })),
    }));
    setResultsFounds('');
    try
    {
      const request = {
        Teams: selectedTeams.map(t => t.value).join(','),
        AsignedTo: selectedEmployees.map(e => e.value).join(','),
        DateTime: moment(fromDate!).format('MM/DD/YYYY'),
        Duedate: moment(toDate!).format('MM/DD/YYYY'),
        projectId: selectedProjects.map(p => p.value).join(','),
        sideMenuId: selectedCardTypes.map(c => c.value).join(','),
        layersId: selectedLayers.map(l => l.value).join(','),
        dashboardsId: '',
        getAll: includeDisabledUsers,
        reportType: String(selectedType.value),
      };
      const resp = await ReportService.GetMonthlyPerformanceReport(request);
      fillTable(resp);
    }
    catch
    {
      setResultsFounds('Error loading data');
    }
    setLoading(false);
  }, [
    validate,
    fromDate,
    toDate,
    selectedTeams,
    selectedEmployees,
    selectedProjects,
    selectedCardTypes,
    selectedLayers,
    selectedType,
    includeDisabledUsers,
    fillTable,
  ]);

  // ── Sorting ──────────────────────────────────────────────────────────────

  const handleSort = (col: SortCol) =>
  {
    const newDir: SortDir = sortCol === col && sortDir === 'asc' ? 'desc' : 'asc';
    setSortCol(col);
    setSortDir(newDir);
    setTableData(prev =>
      [...prev].sort((a, b) =>
      {
        const va =
          col === 'loggedHours' || col === 'productivity' || col === 'efficiency'
            ? parseFloat(a[col])
            : (a[col] as number | string);
        const vb =
          col === 'loggedHours' || col === 'productivity' || col === 'efficiency'
            ? parseFloat(b[col])
            : (b[col] as number | string);
        if (va < vb) return newDir === 'asc' ? -1 : 1;
        if (va > vb) return newDir === 'asc' ? 1 : -1;
        return 0;
      }),
    );
  };

  // ── Render ───────────────────────────────────────────────────────────────

  const colLabel = COLUMN_LABELS[selectedType.value as PerformanceType] ?? 'Day';

  const onchangeType = (itemSelected: SelectOption): void =>
  {
    if (itemSelected == null || itemSelected == undefined)
    {
      return;
    }

    const initialDate = new Date();
    const dueDate = new Date();

    if (itemSelected.label === 'Daily')
    {
      initialDate.setDate(initialDate.getDate() - 2);
    }
    else if (itemSelected.label === 'Weekly')
    {
      initialDate.setDate(initialDate.getDate() - 6);
    }
    else if (itemSelected.label === 'Monthly')
    {
      initialDate.setDate(initialDate.getDate() - 30);
    }
    else if (itemSelected.label === 'Quarterly')
    {
      initialDate.setDate(initialDate.getDate() - 90);
    }
    else if (itemSelected.label === 'Semester')
    {
      initialDate.setDate(initialDate.getDate() - 182);
    }
    else
    {
      initialDate.setDate(initialDate.getDate() - 365);
    }

    setToDate(dueDate);
    setFromDate(initialDate);
    setSelectedType(itemSelected);
  };

  return (
    <>
      <div id="MonthlyPerformanceReportPage" className="overflow-y-auto">
        <h3 className="mpr-title">Performance Report</h3>
        <p className="mpr-subtitle">
          This report contains information about the user's performance.
          <br />
          Dashboard filter will only display information from Default Columns or Dynamic Column,
          never from both.
        </p>

        {/* Top row: filters (left) + chart (top-right) */}
        <div className="mpr-top-row">
          {/* Filters */}
          <div className="mpr-filters">
            <div className="mpr-filter-row">
              <div className="mpr-filter-item">
                <label className="stime-label pull-left">Team(s):</label>
                <Select
                  isMulti
                  options={teamOptions}
                  value={selectedTeams}
                  onChange={v =>
                  {
                    const teams = (v as SelectOption[]) || [];
                    setSelectedTeams(teams);
                    setSelectedEmployees([]);
                    loadEmployees(
                      teams.map(t => t.value),
                      includeDisabledUsers,
                    );
                  }}
                  placeholder="Select..."
                  styles={multiSelectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="mpr-filter-item">
                <label className="stime-label pull-left">Employee(s):</label>
                <Select
                  isMulti
                  options={employeeOptions}
                  value={selectedEmployees}
                  onChange={v => setSelectedEmployees((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={multiSelectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="mpr-filter-item">
                <label className="stime-label pull-left">Card Type(s):</label>
                <Select
                  isMulti
                  options={cardTypeOptions}
                  value={selectedCardTypes}
                  onChange={v => setSelectedCardTypes((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={multiSelectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="mpr-filter-item">
                <label className="stime-label pull-left">Project(s):</label>
                <Select
                  isMulti
                  options={projectOptions}
                  value={selectedProjects}
                  onChange={v => setSelectedProjects((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={multiSelectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
            </div>

            {/* Row 2: Type, Layer, From, To */}
            <div className="mpr-filter-row">
              <div className="mpr-filter-item mpr-filter-item--type">
                <label className="stime-label pull-left">Type:</label>
                <Select
                  options={PERFORMANCE_TYPES}
                  value={selectedType}
                  onChange={v => onchangeType((v as SelectOption) || PERFORMANCE_TYPES[0])}
                  placeholder="Select..."
                  styles={singleSelectStyles}
                  isSearchable={false}
                />
              </div>
              <div className="mpr-filter-item">
                <label className="stime-label pull-left">Layer(s):</label>
                <Select
                  isMulti
                  options={layerOptions}
                  value={selectedLayers}
                  onChange={v => setSelectedLayers((v as SelectOption[]) || [])}
                  placeholder="Select..."
                  styles={multiSelectStyles}
                  closeMenuOnSelect={false}
                  components={{ ClearIndicator }}
                />
              </div>
              <div className="mpr-filter-item mpr-filter-item--date">
                <label className="stime-label pull-left">From:</label>
                <DatePicker
                  selected={fromDate}
                  onChange={(d: any) => setFromDate(d)}
                  maxDate={new Date()}
                  dateFormat="MM/dd/yyyy"
                  customInput={<MprDateInput onClear={() => setFromDate(null)} />}
                  popperProps={{ strategy: 'fixed' }}
                  showPopperArrow={false}
                  calendarClassName="mpr-datepicker-calendar"
                />
                {fromDateEmpty && <span className="mpr-validation">Date is required</span>}
                {fromDateLess && <span className="mpr-validation">From must be before To</span>}
              </div>
              <div className="mpr-filter-item mpr-filter-item--date">
                <label className="stime-label pull-left">To:</label>
                <DatePicker
                  selected={toDate}
                  onChange={(d: any) => setToDate(d)}
                  maxDate={new Date()}
                  dateFormat="MM/dd/yyyy"
                  customInput={<MprDateInput onClear={() => setToDate(null)} />}
                  popperProps={{ strategy: 'fixed' }}
                  showPopperArrow={false}
                  calendarClassName="mpr-datepicker-calendar"
                />
                {toDateEmpty && <span className="mpr-validation">Date is required</span>}
              </div>
            </div>

            {/* Actions: Apply + checkbox */}
            <div className="mpr-filter-actions">
              <button className="mpr-apply-btn" onClick={applyFilters} disabled={loading}>
                {loading ? 'Loading...' : 'Apply Filters'}
              </button>
              <div className="mpr-checkbox-label">
                <span className="corgee_com_checkbox">
                  <input
                    id="mpr-inactive-users-checkbox"
                    type="checkbox"
                    checked={includeDisabledUsers}
                    onChange={() =>
                    {
                      setIncludeDisabledUsers(prev =>
                      {
                        const next = !prev;
                        loadEmployees(
                          selectedTeams.map(t => t.value),
                          next,
                        );
                        return next;
                      });
                    }}
                  />
                  <label htmlFor="mpr-inactive-users-checkbox"></label>
                </span>
                <label htmlFor="mpr-inactive-users-checkbox" className="mpr-checkbox-text">
                  Include Inactive Users
                </label>
              </div>
            </div>
          </div>

          {/* Chart — top right */}
          <div className="mpr-chart-section">
            <Line
              data={chartData}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: true,
                    position: 'top',
                    labels: {
                      color: isDark ? '#a9a093' : 'black',
                      font: { size: 11 },
                      boxWidth: 30,
                    },
                  },
                  tooltip: { enabled: true },
                },
                scales: {
                  x: { ticks: { font: { size: 10 }, color: isDark ? '#a9a093' : '#666' } },
                  y: { ticks: { font: { size: 10 }, color: isDark ? '#a9a093' : '#666' } },
                },
              }}
              height={200}
            />
          </div>
        </div>

        {/* Table — full width */}
        <div className="mpr-content">
          {/* Table */}
          <div className="mpr-table-section">
            <div className="mpr-table-wrapper">
              {/* <div className="mpr-export-row">
              <button
                className="mpr-export-btn"
                onClick={exportToExcel}
                title="Export to Excel"
                disabled={!tableData.length}
              >
                <i className="fa fa-cloud-upload" /> Export
              </button>
            </div> */}
              <div className="mpr-table-scroll">
                <table className="mpr-table">
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('month')} title={colLabel}>
                        {colLabel} <SortIcon col="month" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th
                        onClick={() => handleSort('workingCapacity')}
                        title="Working Capacity"
                        className="w-[145px]"
                      >
                        Working Capacity{' '}
                        <SortIcon col="workingCapacity" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('loggedHours')} title="Logged Hours">
                        Logged Hours <SortIcon col="loggedHours" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('ptoDays')} title="PTO Days">
                        PTO Days <SortIcon col="ptoDays" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('productivity')} title="Productivity">
                        Productivity <SortIcon col="productivity" sorted={sortCol} dir={sortDir} />
                      </th>
                      <th onClick={() => handleSort('efficiency')} title="Efficiency">
                        Efficiency <SortIcon col="efficiency" sorted={sortCol} dir={sortDir} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map(row => (
                      <tr key={row.id}>
                        <td>{row.month}</td>
                        <td>{row.workingCapacity}</td>
                        <td>{row.loggedHours}</td>
                        <td>{row.ptoDays}</td>
                        <td>{row.productivity}</td>
                        <td>{row.efficiency}</td>
                      </tr>
                    ))}
                    {!loading && tableData.length === 0 && (
                      <tr>
                        <td colSpan={6} className="mpr-no-data">
                          {resultsFounds}
                        </td>
                      </tr>
                    )}
                    {loading && (
                      <tr>
                        <td colSpan={6} className="mpr-no-data">
                          Loading...
                        </td>
                      </tr>
                    )}
                  </tbody>
                  {tableData.length > 0 && (
                    <tfoot>
                      <tr>
                        <td>Total</td>
                        <td>{totalWorkingCapacity.toFixed(2)}</td>
                        <td>{totalLoggedHours.toFixed(2)}</td>
                        <td>{totalPtoDays}</td>
                        <td>{totalProductivity.toFixed(2)}</td>
                        <td>{totalEfficiency.toFixed(2)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showDateRangeMsg &&
        createPortal(
          <>
            <style>{`
          @keyframes mpr-date-msg {
            0% { opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { opacity: 0; }
          }
        `}</style>
            <div
              style={{
                position: 'fixed',
                top: 64,
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#62abcb',
                color: '#fff',
                padding: '8px 20px',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                zIndex: 99999,
                pointerEvents: 'none',
                animation: 'mpr-date-msg 3s ease forwards',
              }}
            >
              The start date must be within the last 2 years
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default MonthlyPerformanceReport;
